// src/components/dashboard/client/DossierMessagerie.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, arrayUnion, serverTimestamp, addDoc, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

const DossierMessagerie = ({ dossierId }) => {
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const messagesEndRef = useRef(null);

  // Charger les discussions du dossier
  useEffect(() => {
    if (!dossierId || !user) return;

    console.log('📧 MESSAGERIE: Chargement discussions pour dossier:', dossierId);

    const q = query(
      collection(db, 'discussions'),
      where('dossierId', '==', dossierId),
      orderBy('lastMessage.timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const discussionsData = [];
      
      snapshot.forEach((doc) => {
        const discussion = { id: doc.id, ...doc.data() };
        
        // Vérifier si l'utilisateur est participant
        if (discussion.participants?.includes(user.uid)) {
          discussionsData.push(discussion);
        }
      });

      console.log('📧 MESSAGERIE: Discussions chargées:', discussionsData.length);
      setDiscussions(discussionsData);
      setLoading(false);

      // Sélectionner automatiquement la première discussion
      if (discussionsData.length > 0 && !selectedDiscussion) {
        setSelectedDiscussion(discussionsData[0]);
      }
    });

    return () => unsubscribe();
  }, [dossierId, user]);

  // Charger les messages de la discussion sélectionnée
  useEffect(() => {
    if (!selectedDiscussion) {
      setMessages([]);
      return;
    }

    console.log('📧 MESSAGERIE: Chargement messages pour discussion:', selectedDiscussion.id);

    const q = query(
      collection(db, 'discussions', selectedDiscussion.id, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = [];
      
      snapshot.forEach((doc) => {
        messagesData.push({ id: doc.id, ...doc.data() });
      });

      console.log('📧 MESSAGERIE: Messages chargés:', messagesData.length);
      setMessages(messagesData);

      // Scroll vers le bas automatiquement
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });

    return () => unsubscribe();
  }, [selectedDiscussion]);

  // Marquer la discussion comme lue quand on la sélectionne
  useEffect(() => {
    if (!selectedDiscussion || !user) return;

    const markAsRead = async () => {
      try {
        // Vérifier si déjà lu
        if (selectedDiscussion.readBy?.includes(user.uid)) {
          console.log('📧 MESSAGERIE: Discussion déjà lue');
          return;
        }

        console.log('📧 MESSAGERIE: Marquage comme lue:', selectedDiscussion.id);
        
        const discussionRef = doc(db, 'discussions', selectedDiscussion.id);
        await updateDoc(discussionRef, {
          readBy: arrayUnion(user.uid)
        });

        // Émettre événement pour rafraîchir les compteurs
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts', {
          detail: { type: 'discussion', discussionId: selectedDiscussion.id }
        }));

        console.log('✅ MESSAGERIE: Discussion marquée comme lue');
      } catch (error) {
        console.error('❌ MESSAGERIE: Erreur marquage lu:', error);
      }
    };

    markAsRead();
  }, [selectedDiscussion, user]);

  // Envoyer un nouveau message
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedDiscussion || !user) return;

    try {
      console.log('📧 MESSAGERIE: Envoi message:', newMessage);

      // 1. Ajouter le message en sous-collection
      const messagesRef = collection(db, 'discussions', selectedDiscussion.id, 'messages');
      await addDoc(messagesRef, {
        content: newMessage.trim(),
        senderId: user.uid,
        senderName: user.displayName || user.email,
        timestamp: serverTimestamp()
      });

      // 2. Mettre à jour le document parent pour trigger les listeners
      const discussionRef = doc(db, 'discussions', selectedDiscussion.id);
      await updateDoc(discussionRef, {
        lastMessage: {
          content: newMessage.trim(),
          senderId: user.uid,
          senderName: user.displayName || user.email,
          timestamp: serverTimestamp()
        },
        readBy: [user.uid] // Reset readBy avec seulement l'expéditeur
      });

      // Émettre événement pour rafraîchir les compteurs
      window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts', {
        detail: { type: 'discussion', discussionId: selectedDiscussion.id }
      }));

      setNewMessage('');
      console.log('✅ MESSAGERIE: Message envoyé avec succès');
      
    } catch (error) {
      console.error('❌ MESSAGERIE: Erreur envoi message:', error);
      alert('Erreur lors de l\'envoi du message');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('fr-FR');
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement des discussions...</div>
        </div>
      </div>
    );
  }

  if (discussions.length === 0) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-4">Messagerie du Dossier</h2>
        <div className="bg-gray-100 p-6 rounded-lg text-center">
          <p className="text-gray-600">Aucune discussion trouvée pour ce dossier.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold mb-4">Messagerie du Dossier</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-96">
        {/* Liste des discussions */}
        <div className="bg-white border rounded-lg">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">Discussions ({discussions.length})</h3>
          </div>
          
          <div className="overflow-y-auto h-80">
            {discussions.map((discussion) => {
              const isUnread = !discussion.readBy?.includes(user.uid);
              const isSelected = selectedDiscussion?.id === discussion.id;
              
              return (
                <div
                  key={discussion.id}
                  onClick={() => setSelectedDiscussion(discussion)}
                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                    isSelected ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className={`font-medium ${isUnread ? 'font-bold' : ''}`}>
                          {discussion.title || 'Discussion'}
                        </h4>
                        {isUnread && (
                          <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      
                      {discussion.lastMessage && (
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {discussion.lastMessage.content}
                        </p>
                      )}
                      
                      {discussion.lastMessage?.timestamp && (
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(discussion.lastMessage.timestamp)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Zone de chat */}
        <div className="lg:col-span-2 bg-white border rounded-lg flex flex-col">
          {selectedDiscussion ? (
            <>
              {/* En-tête de la discussion */}
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold">{selectedDiscussion.title || 'Discussion'}</h3>
                <p className="text-sm text-gray-600">
                  {selectedDiscussion.participants?.length} participant(s)
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((message) => {
                  const isOwnMessage = message.senderId === user.uid;
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isOwnMessage 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 text-gray-800'
                      }`}>
                        {!isOwnMessage && (
                          <p className="text-xs font-semibold mb-1">
                            {message.senderName}
                          </p>
                        )}
                        
                        <p className="text-sm">{message.content}</p>
                        
                        <p className={`text-xs mt-1 ${
                          isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {formatDate(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Zone de saisie */}
              <form onSubmit={sendMessage} className="p-4 border-t">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Tapez votre message..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!selectedDiscussion}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Envoyer
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500">Sélectionnez une discussion pour commencer</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DossierMessagerie;